import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Plus, Play, Lock, Layers, Users, Gauge, ChevronRight, Loader2, CheckCircle2, AlertCircle, X, RefreshCw } from "lucide-react";

export default function AdminScoringPage() {
    const { api } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWs, setSelectedWs] = useState("");
    const [cycles, setCycles] = useState([]);
    const [selectedCycle, setSelectedCycle] = useState(null);
    const [tab, setTab] = useState("cadres");
    const [cadres, setCadres] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [scorers, setScorers] = useState([]);
    const [jobDetail, setJobDetail] = useState(null);
    const [composites, setComposites] = useState([]);
    const [compositeDetail, setCompositeDetail] = useState(null);
    const [cohortSummary, setCohortSummary] = useState(null);
    const [displacement, setDisplacement] = useState([]);
    const [displacementDetail, setDisplacementDetail] = useState(null);
    const [verdicts, setVerdicts] = useState({ counts: {}, verdicts: [] });
    const [verdictDetail, setVerdictDetail] = useState(null);
    const [dispSummary, setDispSummary] = useState(null);
    const [busy, setBusy] = useState(false);
    const [newCycleName, setNewCycleName] = useState("");
    const [showNewCycle, setShowNewCycle] = useState(false);

    // Load workspaces + scorers once
    useEffect(() => {
        api.get("/v2/admin/workspaces").then((r) => {
            setWorkspaces(r.data.workspaces || []);
            if (r.data.workspaces?.length && !selectedWs) setSelectedWs(r.data.workspaces[0].id);
        });
        api.get("/v2/admin/scoring/scorers").then((r) => setScorers(r.data.scorers || []));
    }, []);

    const loadCycles = useCallback(async () => {
        if (!selectedWs) return;
        const r = await api.get(`/v2/admin/scoring/cycles/${selectedWs}`);
        setCycles(r.data.cycles || []);
        if (r.data.cycles?.length && (!selectedCycle || !r.data.cycles.find((c) => c.id === selectedCycle.id))) {
            setSelectedCycle(r.data.cycles[0]);
        }
    }, [selectedWs]);

    useEffect(() => { loadCycles(); }, [loadCycles]);

    const loadTabData = useCallback(async () => {
        if (!selectedCycle) return;
        if (tab === "cadres") {
            const r = await api.get(`/v2/admin/scoring/cadres/${selectedCycle.id}`);
            setCadres(r.data.assignments || []);
        } else if (tab === "jobs") {
            const r = await api.get(`/v2/admin/scoring/jobs/${selectedCycle.id}`);
            setJobs(r.data.jobs || []);
        } else if (tab === "composites") {
            const [c, s] = await Promise.all([
                api.get(`/v2/admin/scoring/composites/${selectedCycle.id}`),
                api.get(`/v2/admin/scoring/cohort-summary/${selectedCycle.id}`),
            ]);
            setComposites(c.data.composites || []);
            setCohortSummary(s.data);
        } else if (tab === "displacement") {
            const [c, s] = await Promise.all([
                api.get(`/v2/admin/displacement/composites/${selectedCycle.id}`),
                api.get(`/v2/admin/displacement/cohort-summary/${selectedCycle.id}`),
            ]);
            setDisplacement(c.data.composites || []);
            setDispSummary(s.data);
        } else if (tab === "verdicts") {
            const r = await api.get(`/v2/admin/displacement/verdicts/${selectedCycle.id}`);
            setVerdicts(r.data);
        }
    }, [selectedCycle, tab]);

    useEffect(() => { loadTabData(); }, [loadTabData]);

    // Poll for job progress when on jobs tab
    useEffect(() => {
        if (tab !== "jobs" || !selectedCycle) return;
        const anyActive = jobs.some((j) => j.status !== "completed" && j.status !== "error");
        if (!anyActive) return;
        const t = setInterval(loadTabData, 4000);
        return () => clearInterval(t);
    }, [tab, jobs, selectedCycle, loadTabData]);

    const createCycle = async () => {
        if (!newCycleName.trim() || !selectedWs) return;
        setBusy(true);
        try {
            const r = await api.post("/v2/admin/scoring/cycles", { enterprise_id: selectedWs, name: newCycleName });
            setNewCycleName("");
            setShowNewCycle(false);
            await loadCycles();
            setSelectedCycle(cycles.find((c) => c.id === r.data.id) || r.data);
        } catch (e) {
            alert("Create cycle failed: " + (e.response?.data?.detail || e.message));
        }
        setBusy(false);
    };

    // Phase 7.6.3 — convert cycle gate 400 payloads into human-readable blocker lists.
    const formatGateError = (e) => {
        const d = e.response?.data?.detail;
        if (d && typeof d === "object" && d.error === "preconditions_not_met") {
            const blockers = (d.blockers || []).map(b => "• " + b).join("\n");
            return `Action blocked (${d.action}). Fix these first:\n\n${blockers}`;
        }
        return (typeof d === "string" ? d : null) || e.message;
    };

    const openCycle = async (id) => {
        setBusy(true);
        try { await api.post(`/v2/admin/scoring/cycles/${id}/open`); await loadCycles(); }
        catch (e) { alert(formatGateError(e)); }
        setBusy(false);
    };
    const lockCycle = async (id) => {
        setBusy(true);
        try { await api.post(`/v2/admin/scoring/cycles/${id}/lock`); await loadCycles(); }
        catch (e) { alert(formatGateError(e)); }
        setBusy(false);
    };

    const autoAssignCadres = async () => {
        setBusy(true);
        try {
            await api.post("/v2/admin/scoring/cadres/auto-assign", { cycle_id: selectedCycle.id });
            await loadTabData();
        } catch (e) {
            alert("Auto-assign failed: " + (e.response?.data?.detail || e.message));
        }
        setBusy(false);
    };

    const lockAllCadres = async () => {
        if (!confirm("Lock all cadres for this cycle? This cannot be undone mid-cycle per methodology.")) return;
        setBusy(true);
        await api.post(`/v2/admin/scoring/cadres/${selectedCycle.id}/lock-all`);
        await loadTabData();
        setBusy(false);
    };

    const startScoring = async () => {
        setBusy(true);
        try {
            const r = await api.post("/v2/admin/scoring/jobs/start", { cycle_id: selectedCycle.id });
            setTab("jobs");
            await loadTabData();
            if (r.data.jobs_created === 0) alert("No completed assessments to score (or all already scored).");
        } catch (e) {
            alert(formatGateError(e));
        }
        setBusy(false);
    };

    const openJobDetail = async (id) => {
        const r = await api.get(`/v2/admin/scoring/jobs/detail/${id}`);
        setJobDetail(r.data);
    };

    const computeComposites = async () => {
        setBusy(true);
        try {
            const r = await api.post("/v2/admin/scoring/composites/compute", { cycle_id: selectedCycle.id });
            await loadTabData();
            if (r.data.computed === 0) alert("No completed scoring jobs to compute.");
        } catch (e) {
            alert(formatGateError(e));
        }
        setBusy(false);
    };

    const openCompositeDetail = async (id) => {
        const r = await api.get(`/v2/admin/scoring/composites/detail/${id}`);
        setCompositeDetail(r.data);
    };

    const runDisplacement = async () => {
        setBusy(true);
        try {
            await api.post("/v2/admin/displacement/compute", { cycle_id: selectedCycle.id });
            setTab("displacement");
            setTimeout(loadTabData, 3000);
        } catch (e) {
            alert(formatGateError(e));
        }
        setBusy(false);
    };

    const openDispDetail = async (id) => {
        const r = await api.get(`/v2/admin/displacement/composites/detail/${id}`);
        setDisplacementDetail(r.data);
    };

    const openVerdictDetail = async (id) => {
        const r = await api.get(`/v2/admin/displacement/verdicts/detail/${id}`);
        setVerdictDetail(r.data);
    };

    const cadreColors = {
        "Apprentice": "text-gray-400",
        "Practitioner-IC": "text-white",
        "Senior IC": "text-sky-300",
        "Architect-track": "text-amber-300",
        "Executive-track": "text-emerald-300",
    };

    return (
        <div className="p-8" data-testid="admin-scoring">
            <div className="mb-8">
                <h1 className="text-2xl font-display font-bold text-white">Capability Scoring</h1>
                <p className="text-sm text-gray-500 mt-1">Cycles, cadre assignment, and the AI-scorer pipeline (Opus 4.6 x3 with reconciliation).</p>
            </div>

            {/* Workspace selector */}
            <div className="mb-6 flex items-center gap-3">
                <label className="text-xs text-gray-500 uppercase tracking-wider">Workspace</label>
                <select value={selectedWs} onChange={(e) => { setSelectedWs(e.target.value); setSelectedCycle(null); }}
                    className="bg-muted border border-border/40 px-3 py-1.5 text-sm text-white outline-none" data-testid="workspace-select">
                    {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
            </div>

            {/* Cycles row */}
            <div className="bg-card border border-border/40 p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-white flex items-center gap-2"><Layers className="w-4 h-4 text-gray-500" /> Cycles</h2>
                    <button onClick={() => setShowNewCycle((v) => !v)} className="text-xs text-gray-400 hover:text-white flex items-center gap-1" data-testid="new-cycle-btn"><Plus className="w-3 h-3" /> New cycle</button>
                </div>
                {showNewCycle && (
                    <div className="flex gap-2 mb-3">
                        <input value={newCycleName} onChange={(e) => setNewCycleName(e.target.value)} placeholder="Cycle name (e.g. Q2 2026 Baseline)"
                            className="flex-1 bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none focus:border-white/30" data-testid="cycle-name-input" />
                        <button onClick={createCycle} disabled={busy} className="px-4 py-2 bg-white text-black text-xs font-semibold hover:bg-gray-300 disabled:opacity-50" data-testid="create-cycle-btn">Create</button>
                    </div>
                )}
                {cycles.length === 0 ? (
                    <p className="text-xs text-gray-600">No cycles yet for this workspace.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {cycles.map((c) => (
                            <button key={c.id} onClick={() => setSelectedCycle(c)} data-testid={`cycle-card-${c.id}`}
                                className={`text-left border p-3 transition-colors ${selectedCycle?.id === c.id ? "border-white bg-white/[0.04]" : "border-border/40 hover:border-white/30"}`}>
                                <div className="flex items-start justify-between mb-1">
                                    <p className="text-sm text-white font-semibold">{c.name}</p>
                                    <span className="text-xs font-mono text-gray-500">{c.status}</span>
                                </div>
                                <p className="text-xs text-gray-600 font-mono">jobs {c.jobs_completed}/{c.jobs_total} &middot; {c.methodology_version}</p>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {selectedCycle && (
                <>
                    <div className="mb-5 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-display font-bold text-white">{selectedCycle.name}</h2>
                            <p className="text-xs text-gray-600 font-mono mt-0.5">status: {selectedCycle.status} &middot; methodology: {selectedCycle.methodology_version}</p>
                        </div>
                        <div className="flex gap-2">
                            {selectedCycle.status === "draft" && <button onClick={() => openCycle(selectedCycle.id)} disabled={busy} className="px-3 py-1.5 text-xs bg-white text-black hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1" data-testid="open-cycle-btn"><Play className="w-3 h-3" /> Open cycle</button>}
                            {selectedCycle.status === "active" && <button onClick={() => lockCycle(selectedCycle.id)} disabled={busy} className="px-3 py-1.5 text-xs border border-border/40 text-gray-300 hover:text-white hover:border-white/30 flex items-center gap-1" data-testid="lock-cycle-btn"><Lock className="w-3 h-3" /> Lock cycle</button>}
                        </div>
                    </div>

                    <div className="flex border-b border-border/40 mb-6">
                        {[
                            { id: "cadres", label: `Cadres (${cadres.length})`, icon: Users },
                            { id: "jobs", label: `Scoring Jobs (${jobs.length})`, icon: Gauge },
                            { id: "composites", label: `Capability (${composites.length})`, icon: Gauge },
                            { id: "displacement", label: `Displacement (${displacement.length})`, icon: Gauge },
                            { id: "verdicts", label: `Verdicts (${verdicts.verdicts?.length || 0})`, icon: Gauge },
                            { id: "scorers", label: "Scorer Pool", icon: Layers },
                        ].map((t) => (
                            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                                className={`px-4 py-3 text-sm border-b-2 transition-colors flex items-center gap-2 ${tab === t.id ? "text-white border-white" : "text-gray-500 border-transparent hover:text-white"}`}>
                                <t.icon className="w-3 h-3" /> {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "cadres" && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-xs text-gray-500">Cadres must be locked before scoring begins per §4.2 of the methodology.</p>
                                <div className="flex gap-2">
                                    <button onClick={autoAssignCadres} disabled={busy} className="px-3 py-1.5 text-xs border border-border/40 text-gray-300 hover:text-white hover:border-white/30 flex items-center gap-1" data-testid="auto-assign-cadres-btn"><RefreshCw className="w-3 h-3" /> Auto-assign</button>
                                    <button onClick={lockAllCadres} disabled={busy || cadres.every((c) => c.locked)} className="px-3 py-1.5 text-xs bg-white text-black hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1" data-testid="lock-cadres-btn"><Lock className="w-3 h-3" /> Lock all</button>
                                </div>
                            </div>
                            {cadres.length === 0 ? (
                                <p className="text-sm text-gray-600">No candidates yet. Upload candidates in the workspace, then click Auto-assign.</p>
                            ) : (
                                <div className="bg-card border border-border/40 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted border-b border-border/40">
                                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                                <th className="px-4 py-3">Candidate</th>
                                                <th className="px-4 py-3">Job Title</th>
                                                <th className="px-4 py-3">Cadre</th>
                                                <th className="px-4 py-3">Rationale</th>
                                                <th className="px-4 py-3">Lock</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cadres.map((c) => (
                                                <tr key={c.id} className="border-b border-border/40" data-testid={`cadre-row-${c.candidate_id}`}>
                                                    <td className="px-4 py-3 text-white">{c.candidate_name}</td>
                                                    <td className="px-4 py-3 text-gray-400">{c.job_title}</td>
                                                    <td className={`px-4 py-3 font-mono text-xs ${cadreColors[c.cadre] || "text-gray-300"}`}>{c.cadre}</td>
                                                    <td className="px-4 py-3 text-gray-500 text-xs">{c.rationale}</td>
                                                    <td className="px-4 py-3">{c.locked ? <Lock className="w-3 h-3 text-green-400" /> : <span className="text-xs text-gray-600">—</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "jobs" && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-xs text-gray-500">Each completed assessment is scored by 3 AI personas (Opus 4.6). Reconciliation resolves disagreements automatically.</p>
                                <button onClick={startScoring} disabled={busy} className="px-4 py-2 bg-white text-black text-xs font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1.5" data-testid="start-scoring-btn">
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Start scoring
                                </button>
                            </div>
                            {jobs.length === 0 ? (
                                <p className="text-sm text-gray-600">No scoring jobs yet.</p>
                            ) : (
                                <div className="space-y-2">
                                    {jobs.map((j) => (
                                        <button key={j.id} onClick={() => openJobDetail(j.id)} data-testid={`job-row-${j.id}`}
                                            className="w-full text-left bg-card border border-border/40 p-4 hover:border-white/20 transition-colors flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                {j.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : j.status === "error" ? <AlertCircle className="w-4 h-4 text-red-400" /> : <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />}
                                                <div>
                                                    <p className="text-sm text-white">{j.candidate_name}</p>
                                                    <p className="text-xs text-gray-600 font-mono">{j.candidate_job_title} &middot; {j.status} &middot; {j.scored_responses}/{j.total_responses || '?'} responses</p>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-gray-600" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "composites" && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-xs text-gray-500">MV × 0.30 + TA × 0.25 + HOS × 0.25 + TSA × 0.20 → Values Floor → Segment label. Re-compute is idempotent.</p>
                                <button onClick={computeComposites} disabled={busy} className="px-4 py-2 bg-white text-black text-xs font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1.5" data-testid="compute-composites-btn">
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gauge className="w-3 h-3" />} Compute composites
                                </button>
                            </div>

                            {cohortSummary && cohortSummary.total_candidates > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                                    {["Core Builder", "Emerging Builder", "Support Developer", "Restructuring Candidate"].map((seg) => {
                                        const n = cohortSummary.segment_counts[seg] || 0;
                                        const pct = Math.round((n / cohortSummary.total_candidates) * 100);
                                        return (
                                            <div key={seg} className="bg-card border border-border/40 p-4" data-testid={`cohort-${seg.replace(/\s+/g, '-')}`}>
                                                <p className="text-xs text-gray-600 uppercase tracking-wider">{seg}</p>
                                                <p className="text-2xl font-display font-bold text-white font-mono mt-1">{n}</p>
                                                <p className="text-xs text-gray-500 font-mono">{pct}% of cohort</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {cohortSummary && cohortSummary.total_candidates > 0 && (
                                <div className="bg-card border border-border/40 p-4 mb-6">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Strategic Pathway</p>
                                    <p className="text-sm text-white">{cohortSummary.strategic_pathway}</p>
                                    <p className="text-xs text-gray-500 mt-1 font-mono">Core Builder rate: {cohortSummary.core_builder_rate_pct}%</p>
                                </div>
                            )}

                            {composites.length === 0 ? (
                                <p className="text-sm text-gray-600">No composites yet. Click "Compute composites" after scoring jobs complete.</p>
                            ) : (
                                <div className="bg-card border border-border/40 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted border-b border-border/40">
                                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                                <th className="px-4 py-3">Candidate</th>
                                                <th className="px-4 py-3">Cadre</th>
                                                <th className="px-4 py-3">MV</th>
                                                <th className="px-4 py-3">TA</th>
                                                <th className="px-4 py-3">HOS</th>
                                                <th className="px-4 py-3">TSA</th>
                                                <th className="px-4 py-3">Composite</th>
                                                <th className="px-4 py-3">Segment</th>
                                                <th className="px-4 py-3">Flags</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {composites.map((c) => (
                                                <tr key={c.id} onClick={() => openCompositeDetail(c.id)} className="border-b border-border/40 cursor-pointer hover:bg-white/[0.02]" data-testid={`composite-row-${c.candidate_id}`}>
                                                    <td className="px-4 py-3 text-white">{c.candidate_name}</td>
                                                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">{c.cadre}</td>
                                                    <td className="px-4 py-3 text-gray-300 font-mono text-xs">{c.mv !== null ? c.mv : '—'}</td>
                                                    <td className="px-4 py-3 text-gray-300 font-mono text-xs">{c.ta !== null ? c.ta : '—'}</td>
                                                    <td className="px-4 py-3 text-gray-300 font-mono text-xs">{c.hos !== null ? c.hos : '—'}</td>
                                                    <td className="px-4 py-3 text-gray-300 font-mono text-xs">{c.tsa !== null ? c.tsa : '—'}</td>
                                                    <td className="px-4 py-3 text-white font-mono font-semibold">{c.composite_final}{c.values_floor_applied && <span className="ml-1 text-xs text-amber-300" title="Values Floor applied">⚐</span>}</td>
                                                    <td className="px-4 py-3 text-gray-300 text-xs">{c.segment}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-1 flex-wrap">
                                                            {c.triggered_flags.length === 0 ? <span className="text-xs text-gray-600">—</span> : c.triggered_flags.map((f) => (
                                                                <span key={f} className="text-xs font-mono px-1.5 py-0.5 border border-amber-500/40 text-amber-300">{f}</span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {cohortSummary && Object.keys(cohortSummary.triggered_flag_counts || {}).some((k) => cohortSummary.triggered_flag_counts[k] > 0) && (
                                <div className="mt-6 bg-card border border-border/40 p-4">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Cohort advisory flags</p>
                                    <div className="flex gap-3 flex-wrap">
                                        {Object.entries(cohortSummary.triggered_flag_counts).map(([code, n]) => (
                                            <div key={code} className={`px-3 py-1.5 border text-xs font-mono ${n > 0 ? "border-amber-500/40 text-amber-300" : "border-border/40 text-gray-600"}`}>
                                                {code} <span className="ml-1">{n}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "displacement" && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-xs text-gray-500">Per-task AS/AL/JD scored against each candidate's archetype. Primary composite + 4 modifiers (Tool Exposure, Judgment Concentration, Reskilling Adjacency, Strategic Lock-In) → final band.</p>
                                <button onClick={runDisplacement} disabled={busy} className="px-4 py-2 bg-white text-black text-xs font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1.5" data-testid="run-displacement-btn">
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gauge className="w-3 h-3" />} Run displacement + verdicts
                                </button>
                            </div>

                            {dispSummary && (
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6">
                                    {["High Exposure", "Significant Exposure", "Moderate Exposure", "Low Exposure", "Transforming"].map((b) => {
                                        const n = dispSummary.displacement_band_counts[b] || 0;
                                        return (
                                            <div key={b} className="bg-card border border-border/40 p-3" data-testid={`disp-band-${b.replace(/\s+/g, '-')}`}>
                                                <p className="text-xs text-gray-600 uppercase tracking-wider">{b}</p>
                                                <p className="text-2xl font-display font-bold text-white font-mono mt-1">{n}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {dispSummary && dispSummary.avg_displacement !== undefined && (
                                <div className="bg-card border border-border/40 p-4 mb-6">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider">Cohort avg displacement</p>
                                    <p className="text-2xl font-display font-bold text-white font-mono mt-1">{dispSummary.avg_displacement}</p>
                                </div>
                            )}

                            {displacement.length === 0 ? (
                                <p className="text-sm text-gray-600">No displacement composites yet. Click Run to start the pipeline (uses archetype baselines first, Opus 4.6 fills any missing AS/AL).</p>
                            ) : (
                                <div className="bg-card border border-border/40 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted border-b border-border/40">
                                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                                <th className="px-4 py-3">Candidate</th>
                                                <th className="px-4 py-3">Primary</th>
                                                <th className="px-4 py-3">Tool</th>
                                                <th className="px-4 py-3">JC</th>
                                                <th className="px-4 py-3">RA</th>
                                                <th className="px-4 py-3">SL</th>
                                                <th className="px-4 py-3">Final</th>
                                                <th className="px-4 py-3">Band</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displacement.map((c) => (
                                                <tr key={c.id} onClick={() => openDispDetail(c.id)} className="border-b border-border/40 cursor-pointer hover:bg-white/[0.02]" data-testid={`disp-row-${c.candidate_id}`}>
                                                    <td className="px-4 py-3 text-white">{c.candidate_name}</td>
                                                    <td className="px-4 py-3 text-gray-300 font-mono text-xs">{c.primary}</td>
                                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.mod_tool > 0 ? "+" : ""}{c.mod_tool}</td>
                                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.mod_jc > 0 ? "+" : ""}{c.mod_jc}</td>
                                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.mod_ra > 0 ? "+" : ""}{c.mod_ra}</td>
                                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.mod_sl > 0 ? "+" : ""}{c.mod_sl}</td>
                                                    <td className="px-4 py-3 text-white font-mono font-semibold">{c.composite_final}</td>
                                                    <td className="px-4 py-3 text-gray-300 text-xs">{c.band}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "verdicts" && (
                        <div>
                            <p className="text-xs text-gray-500 mb-4">Deterministic 3×3 lookup: capability band × displacement band → Retain / Retrain / Redeploy. Cadre-aware thresholds per §9.3.</p>

                            {verdicts.counts && (
                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    {["Retain", "Retrain", "Redeploy"].map((v) => (
                                        <div key={v} className="bg-card border border-border/40 p-4" data-testid={`verdict-count-${v}`}>
                                            <p className="text-xs text-gray-600 uppercase tracking-wider">{v}</p>
                                            <p className="text-3xl font-display font-bold text-white font-mono mt-1">{verdicts.counts[v] || 0}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(!verdicts.verdicts || verdicts.verdicts.length === 0) ? (
                                <p className="text-sm text-gray-600">No verdicts yet. Run the Displacement pipeline — it auto-chains verdicts.</p>
                            ) : (
                                <div className="bg-card border border-border/40 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted border-b border-border/40">
                                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                                <th className="px-4 py-3">Candidate</th>
                                                <th className="px-4 py-3">Cadre</th>
                                                <th className="px-4 py-3">Cap</th>
                                                <th className="px-4 py-3">Disp</th>
                                                <th className="px-4 py-3">Verdict</th>
                                                <th className="px-4 py-3">Label</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {verdicts.verdicts.map((v) => {
                                                const shown = v.overridden ? v.override_verdict : v.verdict;
                                                const verdictColor = shown === "Retain" ? "text-emerald-300" : shown === "Retrain" ? "text-amber-300" : "text-sky-300";
                                                return (
                                                    <tr key={v.id} onClick={() => openVerdictDetail(v.id)} className="border-b border-border/40 cursor-pointer hover:bg-white/[0.02]" data-testid={`verdict-row-${v.candidate_id}`}>
                                                        <td className="px-4 py-3 text-white">{v.candidate_name}</td>
                                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{v.cadre}</td>
                                                        <td className="px-4 py-3 text-gray-300 font-mono text-xs">{v.capability} <span className="text-gray-600">({v.capability_band})</span></td>
                                                        <td className="px-4 py-3 text-gray-300 font-mono text-xs">{v.displacement} <span className="text-gray-600">({v.displacement_band})</span></td>
                                                        <td className={`px-4 py-3 font-mono text-sm font-semibold ${verdictColor}`}>{shown}{v.overridden && <span className="ml-1 text-xs">(override)</span>}</td>
                                                        <td className="px-4 py-3 text-gray-500 text-xs">{v.label}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "scorers" && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {scorers.map((s) => (
                                <motion.div key={s.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                    className="bg-card border border-border/40 p-5" data-testid={`scorer-card-${s.persona_key}`}>
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="text-sm font-semibold text-white">{s.name}</h3>
                                        <span className="text-xs font-mono text-gray-500 px-1.5 py-0.5 border border-border/40">{s.role}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-3 leading-relaxed">{s.description}</p>
                                    <p className="text-xs text-gray-600 font-mono">model: {s.model}</p>
                                    <p className="text-xs text-gray-600 font-mono">key: {s.persona_key}</p>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Job detail modal */}
            {jobDetail && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setJobDetail(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-6 border-b border-border/40">
                            <div>
                                <h2 className="text-lg font-display font-bold text-white">Scoring Job Detail</h2>
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 font-mono">
                                    <span>status: {jobDetail.status}</span>
                                    <span>{jobDetail.scored_responses}/{jobDetail.total_responses} scored</span>
                                    <span>{jobDetail.reconciliations?.length || 0} reconciliations</span>
                                </div>
                            </div>
                            <button onClick={() => setJobDetail(null)} className="text-gray-600 hover:text-white" data-testid="close-job-detail-btn"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            <table className="w-full text-sm">
                                <thead className="border-b border-border/40">
                                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                        <th className="px-3 py-2">Layer</th>
                                        <th className="px-3 py-2">Item</th>
                                        <th className="px-3 py-2">A</th>
                                        <th className="px-3 py-2">B</th>
                                        <th className="px-3 py-2">Γ</th>
                                        <th className="px-3 py-2">Final</th>
                                        <th className="px-3 py-2">Δ</th>
                                        <th className="px-3 py-2">Rationale</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(jobDetail.reconciliations || []).map((r, i) => (
                                        <tr key={i} className="border-b border-border/40">
                                            <td className="px-3 py-2 text-gray-400 font-mono text-xs">{r.layer}</td>
                                            <td className="px-3 py-2 text-gray-400 font-mono text-xs">{r.response_key}</td>
                                            <td className="px-3 py-2 text-gray-300 font-mono">{r.score_a ?? "—"}</td>
                                            <td className="px-3 py-2 text-gray-300 font-mono">{r.score_b ?? "—"}</td>
                                            <td className="px-3 py-2 text-gray-300 font-mono">{r.score_tiebreaker ?? "—"}</td>
                                            <td className="px-3 py-2 text-white font-mono font-semibold">{r.final_score}</td>
                                            <td className="px-3 py-2 text-gray-500 text-xs">{r.type}</td>
                                            <td className="px-3 py-2 text-gray-500 text-xs max-w-md">{r.rationale}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                </div>
            )}
            {/* Composite detail modal */}
            {compositeDetail && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setCompositeDetail(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-6 border-b border-border/40">
                            <div>
                                <h2 className="text-lg font-display font-bold text-white">{compositeDetail.candidate.name}</h2>
                                <p className="text-xs text-gray-500 font-mono mt-1">{compositeDetail.candidate.job_title} &middot; {compositeDetail.label}</p>
                            </div>
                            <button onClick={() => setCompositeDetail(null)} className="text-gray-600 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div className="grid grid-cols-4 gap-3">
                                {[
                                    { label: "MV (30%)", value: compositeDetail.layers.mv },
                                    { label: "TA (25%)", value: compositeDetail.layers.ta },
                                    { label: "HOS (25%)", value: compositeDetail.layers.hos },
                                    { label: "TSA (20%)", value: compositeDetail.layers.tsa },
                                ].map((l) => (
                                    <div key={l.label} className="bg-muted border border-border/40 p-4">
                                        <p className="text-xs text-gray-600 uppercase tracking-wider">{l.label}</p>
                                        <p className="text-xl font-display font-bold text-white font-mono mt-1">{l.value !== null ? l.value : '—'}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-muted border border-border/40 p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider">Composite</p>
                                    {compositeDetail.values.floor_applied && <span className="text-xs text-amber-300 font-mono">Values Floor Applied</span>}
                                </div>
                                <p className="text-3xl font-display font-bold text-white font-mono">{compositeDetail.composite_final}<span className="text-sm text-gray-500 font-sans"> / 100</span></p>
                                {compositeDetail.composite_unfloored !== compositeDetail.composite_final && (
                                    <p className="text-xs text-gray-500 mt-1 font-mono">Unfloored: {compositeDetail.composite_unfloored}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-2">{compositeDetail.segment}</p>
                            </div>
                            {compositeDetail.values.pct !== null && (
                                <div className="bg-muted border border-border/40 p-4">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Values Check</p>
                                    <p className="text-sm text-gray-300">Values average: <span className="font-mono text-white">{compositeDetail.values.avg}</span> / 5 &nbsp;·&nbsp; <span className="font-mono text-white">{compositeDetail.values.pct}%</span></p>
                                    {compositeDetail.values_floor_audit && (
                                        <p className="text-xs text-gray-500 mt-1 font-mono">Threshold {compositeDetail.values_floor_audit.threshold}% · cap {compositeDetail.values_floor_audit.floor_cap}</p>
                                    )}
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Advisory Flags</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {compositeDetail.flags.map((f) => (
                                        <div key={f.code} className={`border p-3 ${f.triggered ? "border-amber-500/40" : "border-border/40"}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <p className={`text-sm font-mono ${f.triggered ? "text-amber-300" : "text-gray-400"}`}>{f.code}</p>
                                                <span className={`text-xs font-mono ${f.triggered ? "text-amber-300" : "text-gray-600"}`}>{f.triggered ? "TRIGGERED" : "considered"}</span>
                                            </div>
                                            {f.criteria && <p className="text-xs text-gray-500 font-mono">{JSON.stringify(f.criteria).slice(0, 140)}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
            {/* Displacement detail modal */}
            {displacementDetail && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setDisplacementDetail(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-6 border-b border-border/40">
                            <div>
                                <h2 className="text-lg font-display font-bold text-white">{displacementDetail.candidate.name}</h2>
                                <p className="text-xs text-gray-500 font-mono mt-1">{displacementDetail.candidate.job_title} &middot; {displacementDetail.band}</p>
                            </div>
                            <button onClick={() => setDisplacementDetail(null)} className="text-gray-600 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div className="grid grid-cols-5 gap-2">
                                {[
                                    { label: "Primary", value: displacementDetail.primary },
                                    { label: "Tool", value: displacementDetail.modifiers.tool_exposure },
                                    { label: "JC", value: displacementDetail.modifiers.judgment_concentration },
                                    { label: "RA", value: displacementDetail.modifiers.reskilling_adjacency },
                                    { label: "SL", value: displacementDetail.modifiers.strategic_lock_in },
                                ].map((m) => (
                                    <div key={m.label} className="bg-muted border border-border/40 p-3 text-center">
                                        <p className="text-xs text-gray-600 uppercase tracking-wider">{m.label}</p>
                                        <p className="text-xl font-display font-bold text-white font-mono mt-1">{m.value > 0 ? "+" : ""}{m.value}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-muted border border-border/40 p-4">
                                <p className="text-xs text-gray-600 uppercase tracking-wider">Composite Final</p>
                                <p className="text-3xl font-display font-bold text-white font-mono mt-1">{displacementDetail.composite_final}<span className="text-sm text-gray-500 font-sans"> / 100</span></p>
                                <p className="text-xs text-gray-500 mt-1">{displacementDetail.band}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Per-task breakdown (sorted by displacement desc)</p>
                                <table className="w-full text-sm">
                                    <thead className="border-b border-border/40">
                                        <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                            <th className="px-2 py-2">Task</th>
                                            <th className="px-2 py-2">Time %</th>
                                            <th className="px-2 py-2">AS</th>
                                            <th className="px-2 py-2">AL</th>
                                            <th className="px-2 py-2">JD</th>
                                            <th className="px-2 py-2">Disp</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displacementDetail.tasks.map((t, i) => (
                                            <tr key={i} className="border-b border-border/40">
                                                <td className="px-2 py-2 text-gray-200">{t.task_name}</td>
                                                <td className="px-2 py-2 text-gray-500 font-mono">{t.time_share_pct.toFixed(1)}%</td>
                                                <td className="px-2 py-2 text-gray-300 font-mono">{t.AS}</td>
                                                <td className="px-2 py-2 text-gray-300 font-mono">{t.AL}</td>
                                                <td className="px-2 py-2 text-gray-300 font-mono">{t.JD}</td>
                                                <td className="px-2 py-2 text-white font-mono font-semibold">{t.task_displacement}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Verdict detail modal */}
            {verdictDetail && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setVerdictDetail(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-6 border-b border-border/40">
                            <div>
                                <h2 className="text-lg font-display font-bold text-white">{verdictDetail.candidate.name}</h2>
                                <p className="text-xs text-gray-500 font-mono mt-1">{verdictDetail.label}</p>
                            </div>
                            <button onClick={() => setVerdictDetail(null)} className="text-gray-600 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div className="bg-muted border border-border/40 p-5 text-center">
                                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Verdict</p>
                                <p className={`text-4xl font-display font-bold font-mono ${
                                    (verdictDetail.overridden ? verdictDetail.override_verdict : verdictDetail.verdict) === "Retain" ? "text-emerald-300" :
                                    (verdictDetail.overridden ? verdictDetail.override_verdict : verdictDetail.verdict) === "Retrain" ? "text-amber-300" : "text-sky-300"
                                }`}>{verdictDetail.overridden ? verdictDetail.override_verdict : verdictDetail.verdict}</p>
                                {verdictDetail.overridden && <p className="text-xs text-gray-500 mt-2">Overridden from {verdictDetail.verdict}. Rationale: {verdictDetail.override_rationale}</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-muted border border-border/40 p-4">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider">Capability</p>
                                    <p className="text-2xl font-display font-bold text-white font-mono mt-1">{verdictDetail.capability} <span className="text-xs text-gray-500">({verdictDetail.capability_band})</span></p>
                                </div>
                                <div className="bg-muted border border-border/40 p-4">
                                    <p className="text-xs text-gray-600 uppercase tracking-wider">Displacement</p>
                                    <p className="text-2xl font-display font-bold text-white font-mono mt-1">{verdictDetail.displacement} <span className="text-xs text-gray-500">({verdictDetail.displacement_band})</span></p>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Reasoning</p>
                                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">{verdictDetail.reasoning}</pre>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
